import {
  ArrowLeft,
  CalendarDays,
  CalendarPlus,
  CircleCheck,
  Hammer,
  Lightbulb,
  Loader2,
  type LucideIcon,
  Play,
  Undo2,
} from "lucide-react";
import { Fragment, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { type RoadmapItem, type RoadmapStatusChange, useSetRoadmapStatus } from "@/lib/api";
import { formatMonth } from "@/lib/format";
import { cn } from "@/lib/utils";

import { FinishForm } from "./finish-form";
import {
  type MovedItem,
  type RoadmapStatus,
  statusLabel,
  type StatusMove,
  type StatusMoveOption,
  statusMoves,
} from "./utils";

/** Each status's icon, in front of its items' titles. */
export const STATUS_ICON: Record<RoadmapStatus, LucideIcon> = {
  idea: Lightbulb,
  planned: CalendarDays,
  in_progress: Hammer,
  done: CircleCheck,
};

/** Each move's icon, as on its button in the details. */
const MOVE_ICON: Record<StatusMove, LucideIcon> = {
  plan: CalendarPlus,
  start: Play,
  finish: CircleCheck,
  unplan: Undo2,
  unstart: ArrowLeft,
};

/** The moves that take an item further; the others take it back. */
const FORWARD: ReadonlySet<StatusMove> = new Set(["plan", "start", "finish"]);

/** The symbol in front of a row's title, 1 em, level with the title's first line. */
const SYMBOL_CLASS = "text-primary size-4";

interface StatusSymbolProps {
  item: RoadmapItem;
  /** A developer moves items from the table: the symbol then opens the status menu. */
  isDeveloper: boolean;
  /** The month Inplannen plans an idea in ("2026-11"): the month on show, never a past one. */
  planMonth: string;
  /** A move from the menu failed, for the page to say. */
  onError: (error: unknown) => void;
  /** A move from the menu starts: an earlier failure no longer holds. */
  onStart: () => void;
  /** A move from the menu went through: the item's row left for its new group, and the focus follows it. */
  onMoved: (moved: MovedItem) => void;
}

/**
 * An item's status as the symbol in front of its title, with its name in a
 * tooltip. For a developer, on an item the server lets them move (its `can`),
 * the symbol is a button that opens the status menu (`<StatusMenu>`); for
 * anyone else, and on a done item, it stays a plain symbol. Either carries
 * the item's id (`data-status-of`), so the focus finds it after a move.
 */
export function StatusSymbol({
  item,
  isDeveloper,
  planMonth,
  onError,
  onStart,
  onMoved,
}: StatusSymbolProps) {
  const moves = isDeveloper ? statusMoves(item, planMonth) : [];
  if (moves.length === 0) {
    return <PlainStatus itemId={item.id} status={item.status} />;
  }
  return (
    <StatusMenu
      item={item}
      moves={moves}
      month={planMonth}
      onError={onError}
      onStart={onStart}
      onMoved={onMoved}
    />
  );
}

/** The status's symbol, focusable so a keyboard opens its tooltip, which names the status. */
function PlainStatus({ itemId, status }: { itemId: string; status: RoadmapStatus }) {
  const Icon = STATUS_ICON[status];
  const label = statusLabel(status);
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span
          tabIndex={0}
          data-status={status}
          data-status-of={itemId}
          className="focus-visible:ring-ring mt-0.5 inline-flex shrink-0 rounded-sm focus-visible:ring-2 focus-visible:outline-none"
        >
          <Icon aria-hidden="true" strokeWidth={1.75} className={SYMBOL_CLASS} />
          <span className="sr-only">{label}</span>
        </span>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

interface StatusMenuProps {
  item: RoadmapItem;
  /** The moves the server allows, in the menu's order (`statusMoves`). */
  moves: StatusMoveOption[];
  /** The month Inplannen plans in, for its label. */
  month: string;
  onError: (error: unknown) => void;
  onStart: () => void;
  onMoved: (moved: MovedItem) => void;
}

/**
 * A developer's status menu, on the symbol in front of an item's title: the
 * moves the server allows, forward first. Inplannen plans in the month on
 * show (the current month while a past one is on show), and waits for an
 * effort; Markeer klaar asks for the actual hours in
 * a small dialog first; every other move goes at once. Nothing changes on
 * screen until the server answered and the roadmap refetched.
 *
 * While a move is on its way the symbol spins and opens no menu, but keeps
 * the focus (`aria-disabled`, never `disabled`, which would drop it on the
 * page): after a failure the keyboard is still on the item. After a move
 * that went through, the page sends the focus after the item (`onMoved`).
 */
function StatusMenu({ item, moves, month, onError, onStart, onMoved }: StatusMenuProps) {
  const { t } = useTranslation();
  const status = useSetRoadmapStatus();
  const [menuOpen, setMenuOpen] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const trigger = useRef<HTMLButtonElement>(null);
  // Set while Markeer klaar opens its dialog, so the closing menu leaves the focus to it.
  const opensDialog = useRef(false);
  const Icon = STATUS_ICON[item.status];
  const current = statusLabel(item.status);

  // The promise settles even once the row has left for its new group, where `mutate`'s callbacks would not run.
  const move = async (change: RoadmapStatusChange) => {
    onStart();
    try {
      await status.mutateAsync({ itemId: item.id, change });
    } catch (error) {
      onError(error);
      return;
    }
    onMoved({ id: item.id, status: change.status });
  };

  const label = (option: StatusMoveOption) =>
    option.move === "plan"
      ? t("roadmap.statusMenu.plan", { month: formatMonth(month) })
      : option.move === "finish"
        ? t("roadmap.statusMenu.finish")
        : t(`roadmap.details.${option.move}`);

  return (
    <>
      <Tooltip>
        {/* A move on its way opens no menu. */}
        <DropdownMenu
          open={menuOpen}
          onOpenChange={(open) => setMenuOpen(open && !status.isPending)}
        >
          <DropdownMenuTrigger asChild>
            <TooltipTrigger asChild>
              <button
                ref={trigger}
                type="button"
                aria-disabled={status.isPending || undefined}
                aria-label={t("roadmap.statusMenu.trigger", { status: current, item: item.title })}
                data-status={item.status}
                data-status-of={item.id}
                className="hover:bg-accent-light data-[state=open]:bg-accent-light focus-visible:ring-ring tap-target -mx-1 -mt-0.5 -mb-1 inline-flex shrink-0 rounded-sm p-1 focus-visible:ring-2 focus-visible:outline-none aria-disabled:cursor-wait aria-disabled:opacity-60 pointer-coarse:relative"
              >
                {status.isPending ? (
                  <Loader2 aria-hidden="true" className={cn(SYMBOL_CLASS, "animate-spin")} />
                ) : (
                  <Icon aria-hidden="true" strokeWidth={1.75} className={SYMBOL_CLASS} />
                )}
              </button>
            </TooltipTrigger>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="min-w-52"
            onCloseAutoFocus={(event) => {
              if (opensDialog.current) {
                opensDialog.current = false;
                event.preventDefault();
              }
            }}
          >
            <DropdownMenuLabel className="text-muted text-xs font-normal">
              {current}
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            {moves.map((option, index) => {
              const MoveIcon = MOVE_ICON[option.move];
              const turnsBack =
                index > 0 && !FORWARD.has(option.move) && FORWARD.has(moves[index - 1]!.move);
              return (
                <Fragment key={option.move}>
                  {turnsBack && <DropdownMenuSeparator />}
                  <DropdownMenuItem
                    disabled={option.blocked !== null}
                    data-move={option.move}
                    // A blocked move keeps its reason readable: only its name and icon dim.
                    className="items-start data-[disabled]:opacity-100"
                    onSelect={() => {
                      if (option.change) {
                        void move(option.change);
                      } else {
                        opensDialog.current = true;
                        onStart();
                        setFinishing(true);
                      }
                    }}
                  >
                    <MoveIcon
                      aria-hidden="true"
                      className={cn("mt-0.5", option.blocked ? "text-muted" : "text-primary")}
                    />
                    <span>
                      <span className={cn("block", option.blocked && "text-muted")}>
                        {label(option)}
                      </span>
                      {option.blocked && (
                        <span className="text-muted block text-xs">{t(option.blocked)}</span>
                      )}
                    </span>
                  </DropdownMenuItem>
                </Fragment>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
        <TooltipContent>{t("roadmap.statusMenu.tooltip", { status: current })}</TooltipContent>
      </Tooltip>
      <Dialog open={finishing} onOpenChange={setFinishing}>
        <DialogContent
          className="sm:max-w-md"
          // Back to the symbol, which the menu left for this dialog. Once the item is done its row
          // is gone, and the page puts the focus where the item went.
          onCloseAutoFocus={(event) => {
            event.preventDefault();
            if (trigger.current?.isConnected) {
              trigger.current.focus();
            }
          }}
        >
          <DialogHeader>
            <DialogTitle>{t("roadmap.details.finish")}</DialogTitle>
            <DialogDescription>{item.title}</DialogDescription>
          </DialogHeader>
          <FinishForm
            itemId={item.id}
            onCancel={() => setFinishing(false)}
            onFinished={() => {
              setFinishing(false);
              onMoved({ id: item.id, status: "done" });
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
}
